// /pages/Departments.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { departmentService } from "../services/departmentService";
import ConfirmationDialog from "../components/ui/ConfirmationDialog";
import { toast } from "react-toastify";
import { Page, PageHeader, Section } from "../components/layout/primitives";
import { Button } from '../components/ui/Button';
import { TextArea, TextField } from '../components/ui/Field';
import { Overlay } from '../components/ui/Overlay';
import { Table } from '../components/ui/Table';

type FormValues = {
  name: string;
  description?: string;
};

export const Departments: React.FC = () => {
  const [departments, setDepartments] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editDept, setEditDept] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { name: "", description: "" },
  });

  // Load departments
  const loadDepartments = async () => {
    setLoading(true);
    try {
      const res: any = await departmentService.list({ search: searchTerm });
      const data = res?.departments || res.data || [];
      setDepartments(data);
    } catch (e) {
      toast.error("Failed to load departments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(loadDepartments, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    loadDepartments();
  }, []);

  // Form submit
  const onSubmit = async (values: FormValues) => {
    setSaving(true);
    try {
      if (editDept) {
        const res = await departmentService.update(editDept.id, values);
        toast.success("Department updated!");
      } else {
        await departmentService.create(values);
        toast.success("Department created!");
      }
      setIsCreateOpen(false);
      setEditDept(null);
      loadDepartments();
    } catch (err) {
      toast.error("Error saving department");
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    setEditDept(null);
    reset({ name: "", description: "" });
    setIsCreateOpen(true);
  };

  const openEdit = (dept: any) => {
    setEditDept(dept);
    reset({
      name: dept.name,
      description: dept.description,
    });
    setIsCreateOpen(true);
  };

  const confirmDelete = (id: string) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const doDelete = async () => {
    if (!deleteId) return;
    try {
      await departmentService.remove(deleteId);
      toast.success("Department deleted!");
      setDepartments(prev => prev.filter(d => d.id !== deleteId));
    } catch {
      toast.error("Failed to delete department");
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const filteredDepts = useMemo(() => {
    return departments.filter(d =>
      d.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [departments, searchTerm]);

  return (
    <Page backgroundClassName="bg-gradient-to-br from-agentic-white-off via-agentic-primary-blue/5 to-agentic-accent-purple/10" contentClassName="pt-0">
      <div className="bg-white/80 backdrop-blur-sm border-b shadow-sm">
        <PageHeader
          className="py-6"
          titleClassName="truncate bg-gradient-to-r from-agentic-text-primary to-agentic-accent-purple bg-clip-text text-3xl font-bold text-transparent md:text-3xl"
          subtitleClassName="mt-2 text-agentic-text-secondary text-base"
          title="Departments"
          subtitle="Manage all company departments"
          aside={
            <Button tone="primary-gradient"
              onClick={openCreate}
              className="px-4 py-2 rounded-xl"
            >
              + Add Department
            </Button>
          }
        />
      </div>

      <Section className="py-6">
        <div className="bg-white/80 rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex justify-between items-center">
            <TextField
              type="text"
              placeholder="Search department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-agentic-primary-blue"
            />

            <div className="flex bg-gray-100 rounded-xl p-1 ml-4">
              <Button tone="custom"
                onClick={() => setViewMode("grid")}
                className={`px-4 py-2 rounded-lg text-sm ${
                  viewMode === "grid"
                    ? "bg-white shadow text-agentic-primary-blue"
                    : "text-agentic-text-secondary"
                }`}
              >
                Grid
              </Button>
              <Button tone="custom"
                onClick={() => setViewMode("list")}
                className={`px-4 py-2 rounded-lg text-sm ${
                  viewMode === "list"
                    ? "bg-white shadow text-agentic-primary-blue"
                    : "text-agentic-text-secondary"
                }`}
              >
                List
              </Button>
            </div>
          </div>
        </div>

        {/* List/Grid */}
        {loading ? (
          <div className="text-center py-10">Loading...</div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDepts.map((d) => (
              <div
                key={d.id}
                className="bg-white rounded-2xl shadow-lg p-6 hover:-translate-y-1 transition transform"
              >
                <h3 className="text-lg font-semibold">{d.name}</h3>
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                  {d.description}
                </p>

                <div className="flex mt-4 gap-2">
                  <Button tone="custom"
                    onClick={() => openEdit(d)}
                    className="px-3 py-1 bg-agentic-accent-yellow text-white rounded-lg text-sm"
                  >
                    Edit
                  </Button>

                  <Button tone="custom"
                    onClick={() => confirmDelete(d.id)}
                    className="px-3 py-1 bg-agentic-accent-red text-white rounded-lg text-sm"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // list mode
          <div className="overflow-x-auto rounded-2xl shadow">
            <Table className="w-full bg-white">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-4 text-left">Name</th>
                  <th className="p-4 text-left">Description</th>
                  <th className="p-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDepts.map((d) => (
                  <tr key={d.id} className="border-b">
                    <td className="p-4">{d.name}</td>
                    <td className="p-4">{d.description}</td>
                    <td className="p-4 flex gap-2">
                      <Button tone="custom"
                        onClick={() => openEdit(d)}
                        className="px-3 py-1 bg-agentic-gray-200 rounded-lg text-sm"
                      >
                        Edit
                      </Button>
                      <Button tone="custom"
                        onClick={() => confirmDelete(d.id)}
                        className="px-3 py-1 bg-agentic-accent-red text-white rounded-lg text-sm"
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* Empty state */}
        {!loading && filteredDepts.length === 0 && (
          <div className="text-center py-12">
            <div className="text-5xl mb-3">🏢</div>
            <h3 className="text-xl font-semibold">No departments found</h3>
          </div>
        )}
      </Section>

      {/* Create/Edit Modal */}
      {isCreateOpen && (
        <Overlay className="fixed inset-0 z-50 flex justify-center items-center p-6">
          <div className="absolute inset-0 bg-black/30" onClick={() => setIsCreateOpen(false)}></div>

          <div className="relative bg-white p-6 rounded-2xl shadow-lg w-full max-w-xl">
            <h3 className="text-xl font-bold mb-3">
              {editDept ? "Edit Department" : "Create Department"}
            </h3>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="font-medium">Department Name *</label>
                <TextField
                  {...register("name", { required: "Department name is required" })}
                  className="w-full border px-3 py-2 rounded-lg"
                  placeholder="e.g., Human Resources"
                />
                {errors.name && (
                  <p className="text-red-500 text-xs">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="font-medium">Description</label>
                <TextArea
                  {...register("description")}
                  rows={3}
                  className="w-full border px-3 py-2 rounded-lg"
                  placeholder="Optional description"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button tone="custom"
                  type="button"
                  onClick={() => setIsCreateOpen(false)}
                  className="px-4 py-2 rounded-lg border"
                >
                  Cancel
                </Button>

                <Button tone="custom"
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded-lg text-white bg-gradient-to-r from-agentic-primary-blue to-agentic-primary-blue-light"
                >
                  {saving ? "Saving..." : editDept ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </div>
        </Overlay>
      )}

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={doDelete}
        title="Delete Department?"
        message="This action cannot be undone."
        confirmText="Delete"
        variant="error"
      />
    </Page>
  );
};

export default Departments;
